import { Icon } from '@blueprintjs/core'
import { Menu, MenuItem } from '../ui/menu'
import { Popover } from '../ui/popover'
import classNames from 'classnames'
import { toJS } from 'mobx'
import { observer } from 'mobx-react-lite'
import React, { useState } from 'react'
import { Checkbox } from '../ui/checkbox'
import {
    KeywordAndTargetBreadcrumbs,
    RenderASIN,
    RenderKeywordName,
    RenderTargetName,
} from '../../columns'
import { AdGroupBreadcrumbsComponent } from '../table/AdGroupBreadcrumbs'
import { TargetedProducts as TargetedProductsComponent } from '../../components/products/TargetedProducts'
import { TotalPromotionsNames } from '../../components/products/TotalPromotionsNames'
import type { AdGroup } from '../../types/ad_group'
import type { CampaignPlacement, PerformanceMetrics } from '../../types/campaign'
import type { Keyword } from '../../types/keyword'
import type { Portfolio } from '../../types/portfolio'
import type { ProductAdPerformance } from '../../types/product_ad_extended'
import type { TargetPerformance } from '../../types/target'
import { cents, money, number, percentage } from '../../util/formatters'
import { get, getSuffix, scaledImpactMultiplier } from '../../util/kdp_cols'
import { DefaultBidChange } from '../ad_groups/DefaultBidChange'
import { BidLimitsCell } from '../bid_limits/BidLimitsCell'
import { BidHistoryTooltip } from '../bid_history_tooltip'
import { DeliveryStatusBadge } from '../badge/DeliveryStatusBadge'
import { PlacementAdjustment, resolvePlacement } from '../campaigns/CampaignPlacementsTable'
import type { TempType } from '../campaigns/CampaignsTable'
import { DailyBudgetChange } from '../campaigns/DailyBudgetChange'
import { CampaignBidAdjustmentsEditor } from '../campaigns/CampaignBidAdjustments'
import { SmartBidToggle as SmartBidToggleComponent } from '../campaigns/SmartBidToggle'
import { KeywordBidChange } from '../keywords/KeywordBidChange'
import { SearchTerm } from '../SearchTermsTable'
import { type SearchTermI, searchTermStore } from '../SearchTermsTable2'
import { AcosCell, BlendedAcosCell } from '../table/AcosCell'
import { AovCell, BlendedAovCell } from '../table/AovCell'
import { BlendedCacCell, CacCell } from '../table/CacCell'
import { Calculation, type CalculationLine } from '../table/Calculation'
import { CpcCell } from '../table/CpcCell'
import { CtrCell } from '../table/CtrCell'
import { BlendedCvrCell, CvrCell } from '../table/CvrCell'
import { RenderAdGroupName } from '../table/RenderAdGroupName'
import { RenderCampaignName } from '../table/RenderCampaignName'
import { BlendedRoasCell, RoasCell } from '../table/RoasCell'
import { BlendedRpcCell, RpcCell } from '../table/RpcCell'
import { TargetACOS2, type TargetAcosProps } from '../TargetACOS'
import { TargetBidChange } from '../targets/TargetBidChange'
import { RelativeTimestamp } from '../ui/RelativeTimestamp'
import {
    type AgGridColumnDef,
    DummyHeader,
    type DummyHeaderProps,
    type GlobalContext,
} from './AgGridTable'
import { HeaderContextMenu, isTargetAcosProps } from './react_data_grid_columns'
import { type MarketplaceI, Marketplaces } from '../../util/marketplaces'
import { UpdateReportAdTypes } from '../../util/contstants'
import {
    calculateEffectiveBidRanges,
    formatBidStrategy,
    formatPlacementAdjustment,
} from '../../util/placement_adjustments'

export type ExpandedPerformanceMetrics = PerformanceMetrics & {
    target_id: number
    target_acos?: number
    default_target_acos: number
    target_acos_parent_id: number
    target_acos_parent_type_id: number
    adjusted_orders: number
    adjusted_sales: number
    blended_profit: number
    blended_acos: number
    blended_roas: number
    blended_cvr: number
    blended_cac: number
    blended_aov: number
    blended_rpc: number
    adjusted_pages_read: number
    adjusted_estimated_royalties: number
    top_of_search_impression_share?: number
    ideal_cpc: number
    marketplace?: MarketplaceI
}

export interface CellProps<T> {
    data: T
    context: GlobalContext<T>
    column: {
        colDef: AgGridColumnDef<T>
    }
}

// AG Grid re-invokes a cell renderer only when the row/cell data
// changes, or when the grid `context` reference changes. `checked`
// reads mobx state (`store.selection`), which neither trigger covers.
// Without `observer`, this cell can show a stale checked state after
// a selection change elsewhere: select-all, another row, a bulk
// action (MER-3119).
const SelectCell = observer(
    <T,>({
        data,
        context,
        column: { colDef },
    }: CellProps<T> & { context: { selectKey: keyof T } }) => {
        return (
            <Checkbox
                className="mb-0 m-1"
                checked={context.store.isSelected(context.selectKey, data)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    context.store.selectRow(context.selectKey, data, e.target.checked as boolean)
                }}
            />
        )
    },
)

const CampaignName = ({ data, context }: CellProps<TempType>) => {
    return (
        <RenderCampaignName
            fill
            data={data}
            screenshotMode={context.userHook.screenshotMode}
            mutateItems={(v) => context.store.setItems(v)}
        />
    )
}

const AdGroupName = ({ data, context }: CellProps<AdGroup>) => {
    return (
        <RenderAdGroupName
            fill
            data={data}
            screenshotMode={context.userHook.screenshotMode}
            mutateItems={(v) => context.store.setItems(v)}
        />
    )
}

const AdGroupBreadcrumbs = ({ data, context }: CellProps<AdGroup>) => {
    return (
        <AdGroupBreadcrumbsComponent
            {...data}
            fill
            screenshotMode={context.userHook.screenshotMode}
        />
    )
}

const SmartBidToggle = ({ data, context }: CellProps<TempType>) => {
    return (
        <SmartBidToggleComponent
            key={data.campaign_id}
            campaign={data}
            disabled={false} // @todo ?
            extra={extra(context)}
        />
    )
}

const DailyBudget = ({ data, context }: CellProps<TempType>) => {
    return <DailyBudgetChange disabled={false} campaign={data} extra={extra(context)} />
}

const BidAdjustments = ({ data }: CellProps<TempType>) => {
    return <CampaignBidAdjustmentsEditor campaign={data} />
}

const BidStrategy = ({ data }: CellProps<{ bid_strategy?: string | null }>) =>
    formatBidStrategy(data.bid_strategy) ?? '—'

const DeliveryStatus = ({
    data,
}: CellProps<{
    delivery_status: string | null
    delivery_reasons: string[] | null
    ad_product?: string | null
}>) => (
    <DeliveryStatusBadge
        delivery_status={data.delivery_status}
        delivery_reasons={data.delivery_reasons}
        ad_product={data.ad_product}
    />
)

const PLACEMENT_BID_ADJUSTMENTS = [
    ['bid_adjustment_top_of_search', 'Top of search'],
    ['bid_adjustment_rest_of_search', 'Rest of search'],
    ['bid_adjustment_product_page', 'Product page'],
] as const

const PlacementBidAdjustments = ({
    data,
}: CellProps<Record<(typeof PLACEMENT_BID_ADJUSTMENTS)[number][0], number | null | undefined>>) => {
    const count = PLACEMENT_BID_ADJUSTMENTS.filter(([key]) => (data[key] ?? 0) > 0).length

    return (
        <Popover
            content={
                <div className="p-2">
                    {PLACEMENT_BID_ADJUSTMENTS.map(([key, label]) => (
                        <div key={key} className="flex justify-between gap-4 py-1">
                            <span>{label}</span>
                            <span>{formatPlacementAdjustment(data[key] ?? 0)}</span>
                        </div>
                    ))}
                </div>
            }
            placement="bottom"
            interactionKind="hover"
        >
            <span className="cursor-help">{count}</span>
        </Popover>
    )
}

const DefaultBid = ({ data, context }: CellProps<AdGroup>) => {
    return <DefaultBidChange disabled={false} ad_group={data} extra={extra(context)} />
}

const ProductAdsCount = ({ data, context }: CellProps<TempType>) => {
    const { screenshotMode } = context.userHook
    return (
        <TargetedProductsComponent
            className={classNames({
                blur: screenshotMode,
            })}
            item={data}
        />
    )
}

const LastDailyBudgetChange = ({ data }: CellProps<TempType>) => {
    if (!data.last_daily_budget_change) {
        return null
    }

    return <RelativeTimestamp date={data.last_daily_budget_change} />
}

const LastDefaultBidChange = ({ data }: CellProps<AdGroup>) => {
    if (!data.last_default_bid_change) {
        return null
    }

    return <RelativeTimestamp date={new Date(data.last_default_bid_change)} />
}

const getMarketplaceFromContext = (
    data: ExpandedPerformanceMetrics,
    context: GlobalContext<ExpandedPerformanceMetrics>,
) => {
    let marketplace = data.marketplace
    // @ts-expect-error
    if (context.showTableInBaseCurrency && context.baseCurrency) {
        // Find markeyplace by baseCurrency
        // @ts-expect-error
        marketplace = Object.values(Marketplaces).find(
            (m) => m.currency_code === context.baseCurrency,
        )
    }

    return marketplace
}

const CPC = ({ data, context }: CellProps<ExpandedPerformanceMetrics>) => {
    return CpcCell(
        {
            cpc: data.cpc,
            cost: data.cost,
            clicks: data.clicks,
            marketplace: getMarketplaceFromContext(data, context),
        },
        extra(context),
    )
}

const RPC = ({ data, context }: CellProps<ExpandedPerformanceMetrics>) => {
    return RpcCell(
        {
            rpc: data.rpc,
            sales: data.sales,
            clicks: data.clicks,
            marketplace: getMarketplaceFromContext(data, context),
        },
        extra(context),
    )
}

const BlendedRPC = ({
    data,
    context,
    column: { colDef },
}: CellProps<ExpandedPerformanceMetrics>) => {
    const suffix = getSuffix(colDef.field)
    const g = (key: keyof ExpandedPerformanceMetrics) => get(data, key as string, suffix)

    return BlendedRpcCell({
        blended_rpc: g('blended_rpc'),
        blended_profit: g('blended_profit'),
        clicks: g('clicks'),
        marketplace: getMarketplaceFromContext(data, context),
    })
}

const ACOS = ({ data, context }: CellProps<ExpandedPerformanceMetrics>) => {
    return AcosCell({
        acos: data.acos,
        target_acos: data.target_acos,
        default_target_acos: data.default_target_acos,
        cost: data.cost,
        marketplace: getMarketplaceFromContext(data, context),
    })
}

const BlendedACOS = ({
    data,
    context,
    column: { colDef },
}: CellProps<ExpandedPerformanceMetrics>) => {
    const suffix = getSuffix(colDef.field)
    const g = (key: keyof ExpandedPerformanceMetrics) => get(data, key as string, suffix)

    return BlendedAcosCell({
        ...data,
        cost: g('cost'),
        acos: g('blended_acos'),
        marketplace: getMarketplaceFromContext(data, context),
    })
}

const ROAS = ({ data, context }: CellProps<ExpandedPerformanceMetrics>) => {
    return RoasCell({
        roas: data.roas,
        acos: data.acos,
        target_acos: data.target_acos,
        default_target_acos: data.default_target_acos,
        cost: data.cost,
        marketplace: getMarketplaceFromContext(data, context),
    })
}

const BlendedROAS = ({
    data,
    context,
    column: { colDef },
}: CellProps<ExpandedPerformanceMetrics>) => {
    const suffix = getSuffix(colDef.field)
    const g = (key: keyof ExpandedPerformanceMetrics) => get(data, key as string, suffix)

    return BlendedRoasCell({
        ...data,
        acos: g('blended_acos'),
        roas: g('blended_roas'),
        cost: g('cost'),
        marketplace: getMarketplaceFromContext(data, context),
    })
}

const CTR = ({ data, context }: CellProps<ExpandedPerformanceMetrics>) => {
    return CtrCell(
        {
            ctr: data.ctr,
            impressions: data.impressions,
            clicks: data.clicks,
        },
        extra(context),
    )
}

const CVR = ({ data, context }: CellProps<ExpandedPerformanceMetrics>) => {
    return CvrCell(
        {
            cvr: data.cvr,
            orders: data.orders,
            clicks: data.clicks,
        },
        extra(context),
    )
}

const BlendedCVR = ({
    data,
    column: { colDef },
    context,
}: CellProps<ExpandedPerformanceMetrics>) => {
    const suffix = getSuffix(colDef.field)
    const g = (key: keyof ExpandedPerformanceMetrics) => get(data, key as string, suffix)

    return BlendedCvrCell(
        {
            clicks: g('clicks'),
            adjusted_orders: g('adjusted_orders'),
            blended_cvr: g('blended_cvr'),
        },
        extra(context),
    )
}

const CAC = ({ data, context }: CellProps<ExpandedPerformanceMetrics>) => {
    return CacCell(
        {
            cac: data.cac,
            orders: data.orders,
            cost: data.cost,
        },
        extra(context),
    )
}

const BlendedCAC = ({ data, column: { colDef } }: CellProps<ExpandedPerformanceMetrics>) => {
    const suffix = getSuffix(colDef.field)
    const g = (key: keyof ExpandedPerformanceMetrics) => get(data, key as string, suffix)

    return BlendedCacCell({
        blended_cac: g('blended_cac'),
        adjusted_orders: g('adjusted_orders'),
        cost: g('cost'),
    })
}

const AOV = ({ data, context }: CellProps<ExpandedPerformanceMetrics>) => {
    return AovCell(
        {
            aov: data.aov,
            orders: data.orders,
            sales: data.sales,
        },
        extra(context),
    )
}

const BlendedAOV = ({ data, column: { colDef } }: CellProps<ExpandedPerformanceMetrics>) => {
    const suffix = getSuffix(colDef.field)
    const g = (key: keyof ExpandedPerformanceMetrics) => get(data, key as string, suffix)

    return BlendedAovCell({
        blended_aov: g('blended_aov'),
        adjusted_orders: g('adjusted_orders'),
        blended_profit: g('blended_profit'),
    })
}

const AdjustedPagesRead = ({
    data,
    context,
    column: { colDef },
}: CellProps<ExpandedPerformanceMetrics>) => {
    const suffix = getSuffix(colDef.field)
    const g = (key: keyof ExpandedPerformanceMetrics) => get(data, key as string, suffix)
    const e = extra(context)

    const lines: CalculationLine[] = [
        { description: 'Pages Read', value: number(g('pages_read')) ?? '' },
        {
            description: '* KENP Read Impact Multiplier',
            value:
                number(
                    scaledImpactMultiplier(e?.impact_multiplier?.kenp_read_impact_multiplier),
                    2,
                ) ?? '',
        },
        {
            description: 'Adjusted Pages Read',
            value: number(g('adjusted_pages_read')) ?? '',
        },
    ]
    return <Calculation lines={lines}>{number(g('adjusted_pages_read'))}</Calculation>
}

const AdjustedRoyalties = ({
    data,
    context,
    column: { colDef },
}: CellProps<ExpandedPerformanceMetrics>) => {
    const suffix = getSuffix(colDef.field)
    const g = (key: keyof ExpandedPerformanceMetrics) => get(data, key as string, suffix)
    const e = extra(context)

    const lines: CalculationLine[] = [
        {
            description: 'Estimated Royalties',
            value: money(g('estimated_royalties')) ?? '',
        },
        {
            description: '* KENP Read Impact Multiplier',
            value:
                number(
                    scaledImpactMultiplier(e?.impact_multiplier?.kenp_read_impact_multiplier),
                    2,
                ) ?? '',
        },
        {
            description: 'Adjusted Estimated Royalties',
            value: money(g('adjusted_estimated_royalties')) ?? '',
        },
    ]
    return <Calculation lines={lines}>{money(g('adjusted_estimated_royalties'))}</Calculation>
}

const AdjustedSales = ({
    data,
    context,
    column: { colDef },
}: CellProps<ExpandedPerformanceMetrics>) => {
    const e = extra(context)
    const suffix = getSuffix(colDef.field)
    const g = (key: keyof ExpandedPerformanceMetrics) => get(data, key as string, suffix)
    const m = getMarketplaceFromContext(data, context)

    const lines: CalculationLine[] = [
        { description: 'Sales', value: money(g('sales'), m) ?? '' },
        {
            description: '* Sales Impact Multiplier',
            value:
                number(scaledImpactMultiplier(e?.impact_multiplier?.orders_impact_multiplier), 2) ??
                '',
        },
        { description: 'Adjusted Sales', value: money(g('adjusted_sales'), m) ?? '' },
    ]
    return <Calculation lines={lines}>{money(g('adjusted_sales'), m)}</Calculation>
}

const BlendedProfit = ({
    data,
    context,
    column: { colDef },
}: CellProps<ExpandedPerformanceMetrics>) => {
    const e = extra(context)
    const suffix = getSuffix(colDef.field)
    const g = (key: keyof ExpandedPerformanceMetrics) => get(data, key as string, suffix)
    const m = getMarketplaceFromContext(data, context)

    const lines: CalculationLine[] = [
        { description: '(Sales', value: money(g('sales'), m) ?? '' },
        {
            description: '* Sales Impact Multiplier',
            value:
                number(scaledImpactMultiplier(e?.impact_multiplier?.orders_impact_multiplier), 2) ??
                '',
        },
        {
            description: '* Profit Margin)',
            value:
                percentage(scaledImpactMultiplier(e?.impact_multiplier?.profit_margin, 100.0)) ??
                '',
        },
        {
            description: '+ (Estimated Royalties',
            value: money(g('estimated_royalties'), m) ?? '',
        },
        {
            description: '* KENP Impact Multiplier)',
            value:
                number(
                    scaledImpactMultiplier(e?.impact_multiplier?.kenp_read_impact_multiplier),
                    2,
                ) ?? '',
        },
        { description: 'Blended Profit', value: money(g('blended_profit'), m) ?? '' },
    ]

    return <Calculation lines={lines}>{money(g('blended_profit'), m)}</Calculation>
}

const ASIN = ({ data, context }: CellProps<ProductAdPerformance>) => {
    return <RenderASIN {...data} {...extra(context)} />
}

const Parent = ({ data, context }: CellProps<ProductAdPerformance>) => {
    const { screenshotMode } = context.userHook
    return (
        <KeywordAndTargetBreadcrumbs
            className={classNames({
                blur: screenshotMode,
            })}
            ad_group_status={data.parent_ad_group_status}
            campaign_status={data.parent_campaign_status}
            {...data}
            fill
        />
    )
}

const KeywordCell = ({ data, context }: CellProps<Keyword>) => {
    const { screenshotMode, mutateItems } = extra(context)
    return (
        <RenderKeywordName
            data={data}
            screenshotMode={screenshotMode}
            link
            // @ts-expect-error
            smartBidSettings={data?.smart_bid_setting}
            mutateItems={mutateItems}
            fill
        />
    )
}

const KeywordBidCell = ({ data, context }: CellProps<Keyword>) => {
    return <KeywordBidChange keyword={data} extra={extra(context)} />
}

const TargetBidCell = ({ data, context }: CellProps<TargetPerformance>) => {
    return <TargetBidChange disabled={false} target={data} extra={extra(context)} />
}

const EFFECTIVE_BID_TYPE_BY_KEY: Record<string, number> = {
    ad_group_id: UpdateReportAdTypes.AdGroups,
    keyword_id: UpdateReportAdTypes.Keywords,
    target_id: UpdateReportAdTypes.Targets,
}

const EffectiveBidCell = ({ data, context }: CellProps<any>) => {
    const keyField = context?.selectKey as string
    const type = EFFECTIVE_BID_TYPE_BY_KEY[keyField]
    const baseBid = data.bid ?? data.default_bid
    const ranges = calculateEffectiveBidRanges({
        baseBidCents: baseBid,
        adGroupDefaultBidCents: data.ad_group_default_bid,
        strategy: data.bid_strategy,
        adjustments: {
            topOfSearch: data.bid_adjustment_top_of_search,
            restOfSearch: data.bid_adjustment_rest_of_search,
            // Home page is Sponsored-Brands-only, hidden for now; the backend still returns it.
            productPage: data.bid_adjustment_product_page,
        },
    })

    if (!type || data?.[keyField] == null || !ranges) {
        return <span className="text-gray-300">—</span>
    }

    return (
        <BidHistoryTooltip
            id={data[keyField]}
            type={type}
            campaign_id={data.campaign_id}
            bid={baseBid}
            bid_strategy={data.bid_strategy}
            bid_adjustment_top_of_search={data.bid_adjustment_top_of_search}
            bid_adjustment_rest_of_search={data.bid_adjustment_rest_of_search}
            bid_adjustment_home_page={data.bid_adjustment_home_page}
            bid_adjustment_product_page={data.bid_adjustment_product_page}
            ad_group_default_bid={data.ad_group_default_bid}
        >
            <span className="cursor-pointer">{cents(ranges.ceilingCents)}</span>
        </BidHistoryTooltip>
    )
}

const LastBidChangeCell = ({ data, context }: CellProps<Keyword>) => {
    if (!data.last_bid_change) {
        return null
    }
    return <RelativeTimestamp date={new Date(data.last_bid_change)} />
}

const TopOfSearchImpressionShareCell = ({
    data,
    context,
}: CellProps<ExpandedPerformanceMetrics>) => {
    if (!data.top_of_search_impression_share) {
        return null
    }
    return percentage(data.top_of_search_impression_share / 100, 2)
}

const TargetingExpressionCell = ({ data, context }: CellProps<TargetPerformance>) => {
    const { screenshotMode, mutateItems } = extra(context)
    return (
        <RenderTargetName
            data={data}
            screenshotMode={screenshotMode}
            mutateItems={mutateItems}
            link
            smartBidSettings={data?.smart_bid_setting}
            fill
        />
    )
}

const TotalActivePromotionsNamesCell = ({ data, context }: CellProps<any>) => {
    return (
        <TotalPromotionsNames
            className={classNames({
                blur: context.userHook.screenshotMode,
            })}
            item={data}
            onlyEnabled
        />
    )
}

// @todo type
const TotalPromotionsNamesCell = ({ data, context }: CellProps<any>) => {
    return (
        <TotalPromotionsNames
            className={classNames({
                blur: context.userHook.screenshotMode,
            })}
            item={data}
        />
    )
}

const TargetACOS = ({ data, context }: CellProps<ExpandedPerformanceMetrics>) => {
    const target = context.target
    const product = isTargetAcosProps(data, target)
        ? ({
              ...data,
              target,
          } as TargetAcosProps)
        : null

    if (!product) {
        console.warn('product is not a target acos props', toJS(data), toJS(target))
        return null
    }

    return (
        <TargetACOS2
            // MER-2601: read-only on tables with no per-row Target ACoS entity,
            // so a single edit can't cascade across the campaign. Undefined
            // (e.g. the legacy grid path) stays editable.
            disabled={context.targetAcosEditable === false}
            product={product}
            mutateItems={(v) => context.store.setItems(v)}
        />
    )
}

const PlacementCell = ({ data, context }: CellProps<CampaignPlacement>) => {
    return resolvePlacement(data.placement)
}

const BidAdjustmentCell = ({ data, context }: CellProps<CampaignPlacement>) => {
    return <PlacementAdjustment disabled={false} campaign={data} extra={extra(context)} />
}

const SearchTermCell = ({ data, context }: CellProps<SearchTermI>) => {
    return (
        <SearchTerm
            searchTerm={data}
            extra={extra(context)}
            setSearchTerm={() => searchTermStore.setSearchTerm(data)}
        />
    )
}

const IsNegatedCell = ({ data }: CellProps<SearchTermI>) => {
    return <div>{data.negated ? 'true' : 'false'}</div>
}

const PortfolioNameCell = ({ data, context }: CellProps<Portfolio>) => {
    const { screenshotMode } = context.userHook
    return <div className={classNames({ blur: screenshotMode })}>{data.name}</div>
}

const AdjustedOrders = ({
    data,
    context,
    column: { colDef },
    ...rest
}: CellProps<ExpandedPerformanceMetrics>) => {
    const e = extra(context)
    const suffix = getSuffix(colDef.field)
    const g = (key: keyof ExpandedPerformanceMetrics) => get(data, key as string, suffix)

    const lines: CalculationLine[] = [
        { description: 'Orders', value: number(g('orders')) ?? '' },
        {
            description: '* Sales Impact Multiplier',
            value:
                number(scaledImpactMultiplier(e?.impact_multiplier?.orders_impact_multiplier), 2) ??
                '',
        },
        { description: 'Adjusted Orders', value: number(g('adjusted_orders')) ?? '' },
    ]
    return <Calculation lines={lines}>{number(g('adjusted_orders'))}</Calculation>
}

export const extra = <T,>({ store, userHook }: GlobalContext<T>) => {
    const { hasActiveSubscription, screenshotMode, activeProfile } = userHook
    return {
        hasActiveSubscription,
        screenshotMode,
        impact_multiplier: activeProfile?.impact_multiplier,
        props: undefined,
        reload: () => store.forceReload(),
        mutateItems: (v) => store.setItems(v),
    }
}

const SelectAllHeader = observer(
    <T,>({
        column: { colDef, ...rest },
        context: { store, selectKey },
        ...rest2
    }: DummyHeaderProps<T>) => {
        // <T,>({ store, primaryKey }: { store: TableStore<T>; primaryKey: keyof T }) => {
        const [showSelectAllWidget, setShowSelectAllWidget] = useState(false)
        return (
            <HeaderContextMenu columnName="checkbox" store={store}>
                <Popover
                    isOpen={showSelectAllWidget}
                    position="bottom-right"
                    onClose={() => setShowSelectAllWidget(false)}
                    content={
                        <Menu>
                            {store.allChecked(selectKey) !== true && (
                                <MenuItem
                                    textClassName="pb-1"
                                    onClick={() => store.selectRowsFromCurrentPage(selectKey, true)}
                                    text={'Select ' + store.itemsOnPage + ' rows from this page'}
                                />
                            )}
                            {!store.selection.all &&
                                store.totalItems != store.selection.selectedCount && (
                                    <MenuItem
                                        textClassName="pb-1"
                                        onClick={() => store.selectAll()}
                                        text={'Select ' + store.totalItems + ' rows from all pages'}
                                    />
                                )}
                            {store.selection.selectedCount > 0 && (
                                <MenuItem
                                    textClassName="pb-1"
                                    onClick={() => store.deselectAll()}
                                    text="Deselect all"
                                />
                            )}
                        </Menu>
                    }
                >
                    <div
                        className="inline-flex min-h-[30px] min-w-[30px] cursor-pointer items-center rounded-md bg-transparent px-[10px] py-[5px] text-sm font-normal text-[#1c2127] hover:bg-[rgba(143,153,168,0.15)] active:bg-[rgba(143,153,168,0.3)]"
                        style={{ marginLeft: '-10px' }}
                    >
                        <Checkbox
                            className="mb-0 m-1"
                            checked={store.selection.all || store.allChecked(selectKey) === true}
                            indeterminate={store.allChecked(selectKey) === 'indeterminate'}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                store.selectRowsFromCurrentPage(selectKey, e.target.checked)
                            }
                        />
                        {store.selection.selectedCount > 0 && (
                            <span>{store.selection.selectedCount}</span>
                        )}
                        <span
                            onClick={() => {
                                setShowSelectAllWidget(!showSelectAllWidget)
                            }}
                        >
                            <Icon icon="chevron-down" />
                        </span>
                    </div>
                </Popover>
            </HeaderContextMenu>
        )
    },
)

export const Columns: Record<string, AgGridColumnDef<any>> = {
    TotalCampaigns: {
        field: 'campaigns',
        headerName: 'Campaigns',
        headerComponent: DummyHeader,
        cellClass: 'justify-end',
        align: 'right',
        width: 130,
    },
    EnabledCampaigns: {
        field: 'active_campaigns',
        headerName: 'Enabled Campaigns',
        headerComponent: DummyHeader,
        cellClass: 'justify-end',
        align: 'right',
        width: 170,
    },
    TotalAdGroups: {
        field: 'ad_groups',
        headerName: 'Ad Groups',
        headerComponent: DummyHeader,
        cellClass: 'justify-end',
        align: 'right',
        width: 130,
    },
    EnabledAdGroups: {
        field: 'active_ad_groups',
        headerName: 'Enabled Ad Groups',
        headerComponent: DummyHeader,
        cellClass: 'justify-end',
        align: 'right',
        width: 170,
    },
    AdProduct: {
        field: 'ad_product',
        headerName: 'Product',
        headerComponent: DummyHeader,
        width: 100,
    },
    MetadataSource: {
        field: 'metadata_source',
        headerName: 'Metadata',
        headerComponent: DummyHeader,
        width: 120,
    },
    Select: {
        field: 'checkbox',
        headerName: 'Checkbox',
        headerComponent: SelectAllHeader,
        cellRenderer: SelectCell,
        width: 72,
    },
    CampaignName: {
        field: 'name',
        headerName: 'Campaign',
        headerComponent: DummyHeader,
        cellRenderer: CampaignName,
        width: 335,
    },
    AdGroupName: {
        field: 'name',
        headerName: 'Ad Group',
        headerComponent: DummyHeader,
        cellRenderer: AdGroupName,
        width: 335,
    },
    AdGroupBreadcrumbs: {
        field: 'campaign_name',
        headerName: 'Campaign',
        headerComponent: DummyHeader,
        cellRenderer: AdGroupBreadcrumbs,
        width: 335,
    },
    SmartBidToggle: {
        field: 'smart_bid',
        headerName: 'Smart Bids+',
        headerComponent: DummyHeader,
        cellRenderer: SmartBidToggle,
        width: 130,
    },
    StartDate: {
        field: 'start_date',
        headerName: 'Start Date',
        headerComponent: DummyHeader,
        cellClass: 'justify-end',
        align: 'right',
        width: 125,
    },
    EndDate: {
        field: 'end_date',
        headerName: 'End Date',
        headerComponent: DummyHeader,
        cellClass: 'justify-end',
        align: 'right',
        width: 125,
    },
    ProductAdsCount: {
        field: 'product_ads_count',
        headerName: 'Products',
        headerComponent: DummyHeader,
        cellClass: 'justify-end',
        align: 'right',
        cellRenderer: ProductAdsCount,
        width: 155,
    },
    DailyBudget: {
        field: 'daily_budget',
        headerName: 'Budget',
        headerComponent: DummyHeader,
        cellRenderer: DailyBudget,
        width: 140,
    },
    DefaultBid: {
        field: 'default_bid',
        headerName: 'Bid',
        valueFormatter: (params) => cents(params.value),
        headerComponent: DummyHeader,
        cellRenderer: DefaultBid,
        width: 140,
    },
    BidLimits: {
        field: 'bid_limits',
        headerName: 'Bid Limits',
        headerComponent: DummyHeader,
        cellRenderer: BidLimitsCell,
        width: 150,
    },
    BidAdjustments: {
        field: 'bid_adjustments',
        headerName: 'Bid Adjustments',
        headerComponent: DummyHeader,
        cellRenderer: BidAdjustments,
        align: 'right',
        cellClass: 'justify-end',
        width: 165,
    },
    BidStrategy: {
        field: 'bid_strategy',
        headerName: 'Bid Strategy',
        headerComponent: DummyHeader,
        // The server cannot sort/filter ce-derived fields without putting ce in deferred-join Stage 1.
        sortable: false,
        filter: false,
        cellRenderer: BidStrategy,
        width: 190,
    },
    DeliveryStatus: {
        field: 'delivery_status',
        headerName: 'Delivery Status',
        headerComponent: DummyHeader,
        sortable: true,
        cellRenderer: DeliveryStatus,
        width: 160,
    },
    PlacementBidAdjustments: {
        field: 'bid_adjustment_top_of_search',
        headerName: 'Bid Adjustments',
        headerComponent: DummyHeader,
        // The server cannot sort/filter ce-derived fields without putting ce in deferred-join Stage 1.
        sortable: false,
        filter: false,
        cellRenderer: PlacementBidAdjustments,
        align: 'right',
        cellClass: 'justify-end',
        width: 165,
    },
    LastDailyBudgetChange: {
        field: 'last_daily_budget_change',
        headerName: 'Last Budget Change',
        headerComponent: DummyHeader,
        cellRenderer: LastDailyBudgetChange,
        width: 190,
    },
    LastDefaultBidChange: {
        field: 'last_default_bid_change',
        headerName: 'Last Bid Change',
        headerComponent: DummyHeader,
        cellRenderer: LastDefaultBidChange,
        width: 190,
    },
    CPC: {
        field: 'cpc',
        headerName: 'CPC',
        valueFormatter: (params) => money(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        cellRenderer: CPC,
        width: 90,
    },
    TargetCPC: {
        field: 'ideal_cpc',
        headerName: 'Target CPC',
        headerComponent: DummyHeader,
        valueFormatter: (params) => money(params.value),
        align: 'right',
        cellClass: 'justify-end',
        width: 125,
    },
    RPC: {
        field: 'rpc',
        headerName: 'RPC',
        valueFormatter: (params) => money(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        cellRenderer: RPC,
        width: 90,
    },
    BlendedRPC: {
        field: 'blended_rpc',
        headerName: 'Blended RPC',
        valueFormatter: (params) => money(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        cellRenderer: BlendedRPC,
        width: 130,
    },
    Spend: {
        field: 'cost',
        headerName: 'Spend',
        valueFormatter: (params) => money(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        width: 90,
    },
    Sales: {
        field: 'sales',
        headerName: 'Sales',
        valueFormatter: (params) => money(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        width: 90,
    },
    AdjustedSales: {
        field: 'adjusted_sales',
        headerName: 'Adjusted Sales',
        valueFormatter: (params) => money(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        cellRenderer: AdjustedSales,
        width: 155,
    },
    BlendedProfit: {
        field: 'blended_profit',
        headerName: 'Blended Profit',
        valueFormatter: (params) => money(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        cellRenderer: BlendedProfit,
        width: 145,
    },
    TargetACOS: {
        field: 'target_acos',
        headerName: 'Target ACOS',
        valueFormatter: (params) => percentage(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        cellRenderer: TargetACOS,
        width: 135,
    },
    ACOS: {
        field: 'acos',
        headerName: 'ACOS',
        valueFormatter: (params) => percentage(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        cellRenderer: ACOS,
        width: 90,
    },
    BlendedACOS: {
        field: 'blended_acos',
        headerName: 'Blended ACOS',
        valueFormatter: (params) => percentage(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        cellRenderer: BlendedACOS,
        width: 140,
    },
    ROAS: {
        field: 'roas',
        headerName: 'ROAS',
        valueFormatter: (params) => number(params.value, 2),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        cellRenderer: ROAS,
        width: 90,
    },
    BlendedROAS: {
        field: 'blended_roas',
        headerName: 'Blended ROAS',
        valueFormatter: (params) => number(params.value, 2),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        cellRenderer: BlendedROAS,
        width: 140,
    },
    Impressions: {
        field: 'impressions',
        headerName: 'Impressions',
        valueFormatter: (params) => number(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        width: 130,
    },
    Clicks: {
        field: 'clicks',
        headerName: 'Clicks',
        valueFormatter: (params) => number(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        width: 90,
    },
    CTR: {
        field: 'ctr',
        headerName: 'CTR',
        valueFormatter: (params) => percentage(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        cellRenderer: CTR,
        width: 90,
    },
    Orders: {
        field: 'orders',
        headerName: 'Orders',
        valueFormatter: (params) => number(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        width: 90,
    },
    AdjustedOrders: {
        field: 'adjusted_orders',
        headerName: 'Adj Orders',
        valueFormatter: (params) => number(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        cellRenderer: AdjustedOrders,
        width: 125,
    },
    CVR: {
        field: 'cvr',
        headerName: 'CVR',
        valueFormatter: (params) => percentage(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        cellRenderer: CVR,
        width: 90,
    },
    BlendedCVR: {
        field: 'blended_cvr',
        headerName: 'Blended CVR',
        valueFormatter: (params) => percentage(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        cellRenderer: BlendedCVR,
        width: 130,
    },
    CAC: {
        field: 'cac',
        headerName: 'CAC',
        valueFormatter: (params) => money(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        cellRenderer: CAC,
        width: 90,
    },
    BlendedCAC: {
        field: 'blended_cac',
        headerName: 'Blended CAC',
        valueFormatter: (params) => money(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        cellRenderer: BlendedCAC,
        width: 130,
    },
    AOV: {
        field: 'aov',
        headerName: 'AOV',
        valueFormatter: (params) => money(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        cellRenderer: AOV,
        width: 90,
    },
    BlendedAOV: {
        field: 'blended_aov',
        headerName: 'Blended AOV',
        valueFormatter: (params) => money(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        cellRenderer: BlendedAOV,
        width: 130,
    },
    PagesRead: {
        field: 'pages_read',
        headerName: 'Pages Read',
        valueFormatter: (params) => number(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        width: 130,
    },
    AdjustedPagesRead: {
        field: 'adjusted_pages_read',
        headerName: 'Adj Pages Read',
        valueFormatter: (params) => number(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        cellRenderer: AdjustedPagesRead,
        width: 155,
    },
    EstimatedRoyalties: {
        field: 'estimated_royalties',
        headerName: 'Est Royalties',
        valueFormatter: (params) => money(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        width: 140,
    },
    AdjustedRoyalties: {
        field: 'adjusted_estimated_royalties',
        headerName: 'Adj Royalties',
        valueFormatter: (params) => money(params.value),
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        cellRenderer: AdjustedRoyalties,
        width: 140,
    },
    ASIN: {
        field: 'asin',
        headerName: 'ASIN',
        headerComponent: DummyHeader,
        cellRenderer: ASIN,
        width: 125,
    },
    Parent: {
        field: 'campaign_name',
        headerName: 'Campaign & Ad Group',
        headerComponent: DummyHeader,
        cellRenderer: Parent,
        width: 400,
    },
    Keyword: {
        field: 'keyword_text',
        headerName: 'Keyword',
        headerComponent: DummyHeader,
        cellRenderer: KeywordCell,
        width: 300,
    },
    KeywordBid: {
        field: 'bid',
        headerName: 'Bid',
        headerComponent: DummyHeader,
        cellRenderer: KeywordBidCell,
        width: 130,
    },
    TargetBid: {
        field: 'bid',
        headerName: 'Bid',
        headerComponent: DummyHeader,
        cellRenderer: TargetBidCell,
        width: 130,
    },
    EffectiveBid: {
        field: 'effective_bid',
        headerName: 'Max effective bid',
        headerComponent: DummyHeader,
        // The server cannot sort/filter ce-derived fields without putting ce in deferred-join Stage 1.
        sortable: false,
        filter: false,
        cellRenderer: EffectiveBidCell,
        align: 'right',
        cellClass: 'justify-end',
        width: 150,
    },
    LastBidChange: {
        field: 'last_bid_change',
        headerName: 'Last Bid Change',
        headerComponent: DummyHeader,
        cellRenderer: LastBidChangeCell,
        width: 145,
    },
    TopOfSearchImpressionShare: {
        field: 'top_of_search_impression_share',
        headerName: 'TOS Imp. Share',
        headerComponent: DummyHeader,
        cellRenderer: TopOfSearchImpressionShareCell,
        align: 'right',
        cellClass: 'justify-end',
        width: 125,
    },
    TargetingExpression: {
        field: 'targeting_expression',
        headerName: 'Target',
        headerComponent: DummyHeader,
        cellRenderer: TargetingExpressionCell,
        width: 300,
    },
    TotalActivePromotionsNames: {
        field: 'active_promotions',
        headerName: 'Active Smart Campaigns',
        headerComponent: DummyHeader,
        cellRenderer: TotalActivePromotionsNamesCell,
        align: 'right',
        cellClass: 'justify-end',
        width: 220,
    },
    TotalPromotionsNames: {
        field: 'total_promotions',
        headerName: 'Total Smart Campaigns',
        headerComponent: DummyHeader,
        cellRenderer: TotalPromotionsNamesCell,
        align: 'right',
        cellClass: 'justify-end',
        width: 220,
    },
    ActivePromotionRules: {
        field: 'active_promotion_rules',
        headerName: 'Active Smart Campaign Rules',
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        width: 250,
    },
    TotalPromotionRules: {
        field: 'total_promotion_rules',
        headerName: 'Total Smart Campaign Rules',
        headerComponent: DummyHeader,
        align: 'right',
        cellClass: 'justify-end',
        width: 250,
    },
    Placement: {
        field: 'placement',
        headerName: 'Placement',
        headerComponent: DummyHeader,
        cellRenderer: PlacementCell,
        width: 125,
    },
    BidAdjustment: {
        field: 'percentage',
        headerName: 'Bid Adjustment',
        headerComponent: DummyHeader,
        cellRenderer: BidAdjustmentCell,
        align: 'right',
        cellClass: 'justify-end',
        width: 125,
    },
    SearchTerm: {
        field: 'search_term',
        headerName: 'Breakdown',
        headerComponent: DummyHeader,
        cellRenderer: SearchTermCell,
        width: 400,
    },
    PortfolioName: {
        field: 'name',
        headerName: 'Portfolio',
        headerComponent: DummyHeader,
        cellRenderer: PortfolioNameCell,
        width: 300,
    },
    CampaignCount: {
        field: 'campaigns_count',
        headerName: 'Campaigns',
        headerComponent: DummyHeader,
        width: 125,
    },
}

export const defaultPerfColumns: AgGridColumnDef<any>[] = [
    Columns.CPC,
    Columns.RPC,
    Columns.Spend,
    Columns.Sales,
    Columns.ACOS,
    Columns.ROAS,
    Columns.Impressions,
    Columns.Clicks,
    Columns.CTR,
    Columns.Orders,
    Columns.CVR,
    Columns.CAC,
    Columns.AOV,
]

export const kdpPerfColumns: AgGridColumnDef<any>[] = [
    Columns.PagesRead,
    Columns.EstimatedRoyalties,
]
